import React from 'react';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { Spinner } from '@oysterjs/ui/spinner';
import { messageFromError } from '@oysterjs/core/graphql/errors';
import {
  EmailBulkSendAction,
  EmailBulkSendRecipientSendStatus,
  type EmailBulkSendSummaryFieldsFragment,
  type MailboxProvider
} from '~/api/graphql';
import { cancelEmailBulkSend, changeEmailBulkSendTime } from '~/api/data/email-bulk-send/mutations';
import type { SentRecipient } from '~/api/data/email-bulk-send/queries';
import { OutboxStatusBadge, outboundFailureHeadline } from '~/components/mailbox/outbox-columns';
import type { OutboxStatus } from '~/components/mailbox/outbox-rows';
import { useCurrentAgencyUserId } from '~/components/current-user';
import { InlineNotice } from '~/components/inline-notice';
import { ScheduleSendDialog } from '~/components/schedule-send-dialog';
import { idempotencyKeys } from '~/lib/idempotency';
import { mailClientName } from '~/lib/mailbox-providers';
import { pluralize } from '~/lib/pluralize';
import { MIN_SCHEDULE_LEAD_MS, formatSendTime } from '~/lib/scheduled-send';
import {
  batchByline,
  batchCounts,
  batchSendingHeadline,
  batchStatusLine,
  batchTally,
  heldBackCount,
  isBatchSending
} from './outbox-bulk-send-summary';

type Batch = EmailBulkSendSummaryFieldsFragment;

const NOT_SENT_TITLE = 'Not part of the bulk send.';

/** The hover of an email held back and then sent, scheduled, saved or cancelled on its own. */
const ON_ITS_OWN_TITLE: Partial<Record<EmailBulkSendRecipientSendStatus, string>> = {
  [EmailBulkSendRecipientSendStatus.Scheduled]: 'Scheduled on its own.',
  [EmailBulkSendRecipientSendStatus.InDrafts]: 'Saved on its own.',
  [EmailBulkSendRecipientSendStatus.Cancelled]: 'Cancelled on its own.'
};

/** The Outgoing status each email of a batch is badged with, as its own row badges it. */
const SEND_STATUS_OUTBOX_STATUS: Record<EmailBulkSendRecipientSendStatus, OutboxStatus> = {
  [EmailBulkSendRecipientSendStatus.Scheduled]: 'scheduled',
  [EmailBulkSendRecipientSendStatus.Sending]: 'sending',
  [EmailBulkSendRecipientSendStatus.Sent]: 'sent',
  [EmailBulkSendRecipientSendStatus.Failed]: 'failed',
  [EmailBulkSendRecipientSendStatus.Cancelled]: 'cancelled',
  [EmailBulkSendRecipientSendStatus.InDrafts]: 'inMailbox',
  [EmailBulkSendRecipientSendStatus.SaveFailed]: 'failed',
  [EmailBulkSendRecipientSendStatus.HeldBack]: 'composed'
};

export const BatchProgressLoadFailure = ({
  className,
  onReload
}: {
  className?: string;
  onReload: () => void;
}) => (
  <InlineNotice
    className={className}
    tone="warning"
    title="We couldn't load this batch's progress."
    description="The emails below are up to date. Reload to try again."
    actions={
      <Button color="white" onClick={onReload}>
        Reload
      </Button>
    }
  />
);

/**
 * One recipient's status, badged as their email's own Outgoing row is. One with no status, as
 * one Cara couldn't draft, wasn't part of the send.
 */
export const BulkSendRecipientStatusBadge = ({
  recipient,
  batch
}: {
  recipient: SentRecipient;
  batch: Pick<Batch, 'action' | 'firstSendAt'>;
}) => {
  const { sendStatus } = recipient;
  const { action } = batch;
  if (!sendStatus) {
    return (
      <Badge color="neutral" title={recipient.heldBackReason ?? NOT_SENT_TITLE}>
        Not sent
      </Badge>
    );
  }
  if (sendStatus === EmailBulkSendRecipientSendStatus.HeldBack) {
    return (
      <OutboxStatusBadge
        status="composed"
        heldBack={{ reason: recipient.heldBackReason ?? null }}
      />
    );
  }
  if (sendStatus === EmailBulkSendRecipientSendStatus.SaveFailed) {
    return <Badge color="red">Couldn&apos;t save to drafts</Badge>;
  }
  // An email sent on its own waits for its own time, not its batch's turn.
  const { isOutsideBatch } = recipient;
  const isSavingToDrafts =
    !isOutsideBatch &&
    action === EmailBulkSendAction.MoveToDrafts &&
    sendStatus === EmailBulkSendRecipientSendStatus.Scheduled;
  if (isSavingToDrafts) {
    return <Badge color="neutral">Saving to drafts</Badge>;
  }
  const failureTitle = recipient.sendFailureKind
    ? outboundFailureHeadline(recipient.sendFailureKind)
    : undefined;
  const onItsOwnTitle = ON_ITS_OWN_TITLE[sendStatus] ?? 'Sent on its own.';
  const isScheduledWithBatch =
    !isOutsideBatch &&
    action === EmailBulkSendAction.ScheduleAll &&
    sendStatus === EmailBulkSendRecipientSendStatus.Scheduled;
  const firstSendAt = batch.firstSendAt ? new Date(batch.firstSendAt) : null;
  const batchSendTitle =
    isScheduledWithBatch && firstSendAt
      ? `Sends with the batch, starting ${formatSendTime(firstSendAt)}.`
      : undefined;
  const title = isOutsideBatch
    ? [onItsOwnTitle, failureTitle].filter(Boolean).join(' ')
    : (failureTitle ?? batchSendTitle);
  return (
    <OutboxStatusBadge
      status={SEND_STATUS_OUTBOX_STATUS[sendStatus]}
      isQueued={!isOutsideBatch && action !== EmailBulkSendAction.ScheduleAll}
      title={title}
    />
  );
};

/** Who sent the batch, how far it has got, Cancel the rest and, while it waits, Change time. */
export const SentBatchSummary = ({
  batch,
  mailboxProviders,
  sendRest,
  onChanged
}: {
  batch: Batch;
  mailboxProviders: ReadonlyMap<string, MailboxProvider>;
  /** Send the rest, in Cancel's place once the round has nothing left to cancel. */
  sendRest?: React.ReactNode;
  /** Reads the page again once the batch is cancelled or its time changes. */
  onChanged: () => void;
}) => {
  const currentUserId = useCurrentAgencyUserId();
  const [isConfirmingCancel, setIsConfirmingCancel] = React.useState(false);
  const [isChangingTime, setIsChangingTime] = React.useState(false);

  const { progress } = batch;
  const now = new Date();
  const { waiting } = batchTally(batch);
  const firstSendAtMs = batch.firstSendAt ? new Date(batch.firstSendAt).getTime() : 0;
  const sentAtMs = batch.sentAt ? new Date(batch.sentAt).getTime() : 0;
  const sendingHeadline = batchSendingHeadline(batch);
  const headlineExtraCounts = [
    heldBackCount(batch),
    { count: progress.notSent, label: 'not sent' }
  ].filter(({ count }) => count > 0);
  const isSender = !!batch.sentBy && batch.sentBy.id === currentUserId;
  const isMoveToDrafts = batch.action === EmailBulkSendAction.MoveToDrafts;
  const isSending = isBatchSending(
    { waiting, firstSendAtMs, sentAtMs, isWaitingForMailbox: batch.isWaitingForMailbox },
    now.getTime()
  );
  // The email being sent can't be cancelled, so it alone leaves nothing to cancel.
  const canCancelTheRest = !batch.cancelledAt && !isMoveToDrafts && waiting > progress.sending;
  const hasNothingGoneOut = batch.cancelReturnsToDraft;
  const canChangeTime = canCancelTheRest && batch.canChangeTime;
  const mailClient = mailClientName(mailboxProviders.get(batch.mailboxAccountId ?? ''));
  const statusLine = batchStatusLine(batch, { now, mailClient });

  // Every part keeps its height as the batch moves from waiting to sending to done, so the
  // recipients table under it stays still.
  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="m-0 min-w-0 text-sm text-neutral-500 dark:text-neutral-400">
          {batchByline(batch, now)}
        </p>
        {/* As tall as Cancel the rest, with it or without. */}
        <div className="flex min-h-11 shrink-0 items-center gap-2 sm:min-h-9">
          {canCancelTheRest &&
            (isSender ? (
              <>
                {canChangeTime && (
                  <Button color="white" onClick={() => setIsChangingTime(true)}>
                    Change time
                  </Button>
                )}
                <Button color="white" onClick={() => setIsConfirmingCancel(true)}>
                  {hasNothingGoneOut ? 'Cancel the send' : 'Cancel the rest'}
                </Button>
              </>
            ) : (
              <p className="m-0 text-sm text-neutral-500 dark:text-neutral-400">
                Only {batch.sentBy?.name ?? 'the sender'} can cancel it.
              </p>
            ))}
          {!canCancelTheRest && sendRest}
        </div>
      </div>
      {/* One live region for both forms, so a screen reader hears the switch between them. */}
      {/* Wraps on a narrow screen rather than cutting off the last counts. */}
      <div role="status" className="mt-3 min-h-5">
        {sendingHeadline ? (
          <div className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-neutral-900 dark:text-white">
            {sendingHeadline}
            {headlineExtraCounts.map(({ count, label }) => (
              <span key={label} className="font-normal text-neutral-700 dark:text-neutral-300">
                <span aria-hidden>·</span> {count} {label}
              </span>
            ))}
            {isSending && (
              <span aria-hidden>
                <Spinner size="size-4" color="sky" />
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 text-sm text-neutral-700 dark:text-neutral-300">
            {batchCounts(batch).map(({ count, label }) => (
              <span key={label}>
                <span className="font-semibold text-neutral-900 dark:text-white">{count}</span>{' '}
                {label}
              </span>
            ))}
            {isSending && (
              <>
                <span className="sr-only">Sending</span>
                <span aria-hidden>
                  <Spinner size="size-4" color="sky" />
                </span>
              </>
            )}
          </div>
        )}
      </div>
      <p
        className="m-0 mt-2 truncate text-sm text-neutral-500 dark:text-neutral-400"
        title={statusLine}
      >
        {statusLine}
      </p>
      <CancelTheRestDialog
        open={isConfirmingCancel}
        bulkSendId={batch.id}
        hasNothingGoneOut={hasNothingGoneOut}
        sentCount={progress.sent}
        sendingCount={progress.sending}
        cancellableCount={waiting - progress.sending}
        onCancelled={onChanged}
        onClose={() => setIsConfirmingCancel(false)}
      />
      {isChangingTime && (
        <ChangeTimeDialog
          batch={batch}
          provider={mailboxProviders.get(batch.mailboxAccountId ?? '')}
          onChanged={onChanged}
          onClose={() => setIsChangingTime(false)}
        />
      )}
    </div>
  );
};

const CHECK_CONNECTION = 'Check your connection and try again.';

const minuteOf = (time: Date) => Math.floor(time.getTime() / 60_000);

/** Schedule all's picker, for a batch none of whose emails has gone out. */
const ChangeTimeDialog = ({
  batch,
  provider,
  onChanged,
  onClose
}: {
  batch: Batch;
  provider: MailboxProvider | undefined;
  onChanged: () => void;
  onClose: () => void;
}) => {
  // A retry of the same time reuses its key, so it moves the batch once.
  const attemptIds = React.useRef(idempotencyKeys());
  const [isChanging, setIsChanging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const currentStart = batch.firstSendAt ? new Date(batch.firstSendAt) : undefined;
  // A start too soon to pick again opens on the first quick pick rather than on an error.
  const [openedAtMs] = React.useState(() => Date.now());
  const isCurrentStartPickable =
    !!currentStart && currentStart.getTime() - openedAtMs >= MIN_SCHEDULE_LEAD_MS;

  const change = async (sendAt: Date) => {
    // The picker holds whole minutes, so the current start comes back without its seconds.
    const isSameTime = !!currentStart && minuteOf(currentStart) === minuteOf(sendAt);
    if (isSameTime) {
      onClose();
      return;
    }
    setIsChanging(true);
    setError(null);
    let failure: string | null = null;
    try {
      const attemptId = attemptIds.current.keyFor({ sendAt: sendAt.toISOString() });
      const result = await changeEmailBulkSendTime({ id: batch.id, input: { attemptId, sendAt } });
      failure = result.error ? messageFromError(result.error, CHECK_CONNECTION) : null;
      if (result.error) {
        // A refused change leaves nothing a retry should return, so trying again is a new attempt.
        attemptIds.current.reset();
      }
    } catch (changeError) {
      failure = messageFromError(changeError, CHECK_CONNECTION);
    }
    setIsChanging(false);
    // Read either way: a change that couldn't finish may have made the batch a draft again.
    onChanged();
    if (failure) {
      setError(failure);
    } else {
      onClose();
    }
  };

  return (
    <ScheduleSendDialog
      mode="schedule"
      provider={provider}
      submitting={isChanging}
      error={error}
      initialSendAt={isCurrentStartPickable ? currentStart : undefined}
      onSubmit={change}
      onClose={onClose}
      batchWording={{
        title: 'Change time',
        submitLabel: 'Change time',
        errorTitle: "We couldn't change the time.",
        sendTimeLabel: 'Starts',
        tooSoonMessage:
          'Pick a time at least 5 minutes from now, or cancel the send and use Send all.',
        footnote: currentStart
          ? `Now starts ${formatSendTime(currentStart)}. Sending from ${batch.fromAddress}.`
          : `Sending from ${batch.fromAddress}.`
      }}
    />
  );
};

const CancelTheRestDialog = ({
  open,
  bulkSendId,
  hasNothingGoneOut,
  sentCount,
  sendingCount,
  cancellableCount,
  onCancelled,
  onClose
}: {
  open: boolean;
  bulkSendId: string;
  hasNothingGoneOut: boolean;
  sentCount: number;
  sendingCount: number;
  cancellableCount: number;
  onCancelled: () => void;
  onClose: () => void;
}) => {
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [error, setError] = React.useState<string>();

  const cancel = async () => {
    setIsCancelling(true);
    setError(undefined);
    try {
      const result = await cancelEmailBulkSend({ id: bulkSendId });
      if (result.error) {
        setError(messageFromError(result.error, 'Check your connection and try again.'));
        return;
      }
      onCancelled();
      onClose();
    } catch (cancelError) {
      setError(messageFromError(cancelError, 'Check your connection and try again.'));
    } finally {
      setIsCancelling(false);
    }
  };

  if (hasNothingGoneOut) {
    return (
      <Dialog open={open} onClose={isCancelling ? () => {} : onClose} size="md">
        <DialogTitle>Cancel the scheduled send?</DialogTitle>
        <DialogBody className="space-y-3 text-sm text-neutral-700 dark:text-neutral-300">
          {error && (
            <InlineNotice tone="error" title="We couldn't cancel the send." description={error} />
          )}
          <p>The emails go back to drafts here.</p>
        </DialogBody>
        <DialogActions>
          <Button color="white" disabled={isCancelling} onClick={onClose}>
            Keep it scheduled
          </Button>
          <Button color="red" loading={isCancelling} onClick={cancel}>
            Cancel the send
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={isCancelling ? () => {} : onClose} size="md">
      <DialogTitle>
        Cancel the other {cancellableCount} {pluralize(cancellableCount, 'email')}?
      </DialogTitle>
      <DialogBody className="space-y-3 text-sm text-neutral-700 dark:text-neutral-300">
        {error && (
          <InlineNotice tone="error" title="We couldn't cancel the rest." description={error} />
        )}
        <p>
          {sentCount === 0
            ? 'None have gone out yet.'
            : `${sentCount} already went out and can't be called back.`}{' '}
          {sendingCount > 0 && 'The one being sent now still goes out. '}
          The other {cancellableCount} won&apos;t be sent and{' '}
          {pluralize(cancellableCount, 'becomes a draft', 'become drafts')} again.
        </p>
      </DialogBody>
      <DialogActions>
        <Button color="white" disabled={isCancelling} onClick={onClose}>
          Keep sending
        </Button>
        <Button color="red" loading={isCancelling} onClick={cancel}>
          Cancel {cancellableCount} {pluralize(cancellableCount, 'email')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
